import { dataFetchingClient } from '../../utils/DataFetchingClient';
import { registerQuery, useQuery } from 'academy-common-lib/utils/DataFetchingClient';
import { quickFetchOrGet, getCachedThenNew } from '../../utils/ajaxUtils';
import { ACADEMY_CATALOG_SERVICE_URI, ACADEMY_CATALOG_SERVICE_URI_V2 } from '../../constants/ApiConstants';
import getLang from 'LearningCenterUI/utils/getLang';
import { useMemo } from 'react';
import { updateLessonCacheChirp } from '../lessons';
import { updateFullLessonCache, updatePartialLessonCache } from '../lessons/lessonQueries';
import { useIsPartner } from '../partners/partnersQueries';
import pick from 'hs-lodash/pick';
import { useGetHydratedTrackQuery } from '../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/TrackChirpService.hooks';
import { convertToSupportedLanguage } from 'academy-common-lib/constants/academySupportedLanguages';
import { getLanguagesFromLinkedContent } from '../../utils/languageUtils';
const normalizeLesson = lesson => Object.assign({
  contentIds: lesson.content.map(content => content.id),
  isRequesting: false,
  hasError: false
}, pick(lesson, ['deprecationCta', 'deprecationFinalDate', 'deprecationMessage', 'deprecationWarningDate', 'description', 'duration', 'hsSoftwareFilters', 'id', 'internalName', 'language', 'lessonType', 'levelFilters', 'name', 'previewThumbnailRectangle', 'previewThumbnailRectangleUrl', 'previewThumbnailSquare', 'previewThumbnailSquareUrl', 'subCategories', 'visibility']));
export const GET_TRACK = registerQuery({
  fieldName: 'track',
  args: ['trackId', 'language'],
  fetcher({
    trackId,
    language
  }) {
    return quickFetchOrGet(`${ACADEMY_CATALOG_SERVICE_URI}/tracks/${trackId}/${language}`).then(response => response);
  }
});
export const fetchTrackById = (trackId, language) => {
  return dataFetchingClient.queryUnwrapped({
    query: GET_TRACK,
    variables: {
      trackId,
      language: language.toLowerCase()
    }
  }).then(track => {
    track.lessons.forEach(lesson => {
      updateFullLessonCache(lesson, language);
      updatePartialLessonCache(normalizeLesson(lesson), language);
    });
    return track;
  });
};
export const useFullTrack = (trackId, language, skip) => {
  if (!language || !language.length) language = getLang();
  const {
    data: track,
    loading: trackLoading,
    error: trackError
  } = useQuery(GET_TRACK, {
    variables: {
      trackId: trackId === null || trackId === void 0 ? void 0 : trackId.toString(),
      language: language.toLowerCase()
    },
    skip: skip || trackId === null || isNaN(trackId),
    onCompleted(data) {
      data.track.lessons.forEach(lesson => {
        updateFullLessonCache(lesson, language);
        updatePartialLessonCache(normalizeLesson(lesson), language);
      });
    }
  });
  return {
    track,
    trackLoading,
    trackError
  };
};
export const useFullChirpTrack = (trackId, language, skip) => {
  const supportedLanguage = convertToSupportedLanguage(language);
  const {
    data,
    loading: trackLoading,
    error: trackError
  } = useGetHydratedTrackQuery({
    variables: {
      trackId,
      language: supportedLanguage
    },
    skip: skip || trackId === null || isNaN(trackId),
    onCompleted(trackData) {
      trackData.result.track.lessons.forEach(lesson => {
        updateLessonCacheChirp(lesson);
      });
    }
  });
  return {
    track: data === null || data === void 0 ? void 0 : data.track,
    trackLoading,
    trackError
  };
};
export const useAvailableLanguagesByTrack = (trackId, skip, language) => {
  const {
    data: hydratedTrack,
    loading: trackLanguagesLoading,
    error: trackLanguagesError
  } = useGetHydratedTrackQuery({
    variables: {
      trackId,
      language: (language || getLang()).toUpperCase()
    },
    skip
  });
  const trackLanguages = useMemo(() => {
    var _hydratedTrack$track, _hydratedTrack$track2;
    return getLanguagesFromLinkedContent(hydratedTrack === null || hydratedTrack === void 0 || (_hydratedTrack$track = hydratedTrack.track) === null || _hydratedTrack$track === void 0 || (_hydratedTrack$track = _hydratedTrack$track.languageLinkedContent) === null || _hydratedTrack$track === void 0 ? void 0 : _hydratedTrack$track.languageToId, hydratedTrack === null || hydratedTrack === void 0 || (_hydratedTrack$track2 = hydratedTrack.track) === null || _hydratedTrack$track2 === void 0 ? void 0 : _hydratedTrack$track2.language);
  }, [hydratedTrack]);
  return {
    trackLanguages,
    trackLanguagesLoading,
    trackLanguagesError
  };
};
export const GET_ALL_PUBLIC_TRACKS = registerQuery({
  fieldName: 'allPublicTracks',
  args: ['language'],
  fetcher({
    language
  }) {
    const lang = language || getLang();
    return getCachedThenNew(`tracks-${lang}`, `${ACADEMY_CATALOG_SERVICE_URI_V2}/tracks/${lang}?fallbackLanguage=en`).then(response => response);
  }
});
export const useAllPublicTracks = (language, skip = false) => {
  const {
    data: allPublicTracks,
    loading: allPublicTracksLoading,
    error: allPublicTracksError
  } = useQuery(GET_ALL_PUBLIC_TRACKS, {
    variables: {
      language: language.toLowerCase()
    },
    skip
  });
  return {
    allPublicTracks: allPublicTracks ? allPublicTracks.result : null,
    allPublicTracksLoading,
    allPublicTracksError
  };
};
const GET_ALL_PARTNER_TRACKS = registerQuery({
  fieldName: 'allPartnerTracks',
  args: ['language'],
  fetcher({
    language
  }) {
    return quickFetchOrGet(`partner-tracks-${language}`, `${ACADEMY_CATALOG_SERVICE_URI}/tracks/partners/${language}?fallbackLanguage=en`).then(response => {
      return response;
    });
  }
});
export const fetchAllPartnerTracks = ({
  language
}) => {
  return dataFetchingClient.queryUnwrapped({
    query: GET_ALL_PARTNER_TRACKS,
    variables: {
      language: language.toLowerCase()
    }
  });
};
export const useAllPartnerTracks = (language, skip = false) => {
  const isPartner = useIsPartner();
  const {
    data: allPartnerTracks,
    loading: allPartnerTracksLoading,
    error: allPartnerTracksError
  } = useQuery(GET_ALL_PARTNER_TRACKS, {
    variables: {
      language: language.toLowerCase()
    },
    skip: !isPartner || skip
  });
  return {
    allPartnerTracks,
    allPartnerTracksLoading,
    allPartnerTracksError
  };
};
export const useAllTracks = (language = getLang(), skip = false) => {
  const {
    allPublicTracks,
    allPublicTracksLoading,
    allPublicTracksError
  } = useAllPublicTracks(language, skip);
  const {
    allPartnerTracks,
    allPartnerTracksLoading,
    allPartnerTracksError
  } = useAllPartnerTracks(language, skip);
  const allTracks = useMemo(() => {
    const allTracksArray = [...(Array.isArray(allPublicTracks) ? allPublicTracks : []), ...(Array.isArray(allPartnerTracks) ? allPartnerTracks : [])];
    return allTracksArray.reduce((currentTracks, track) => {
      currentTracks[track.id] = track;
      return currentTracks;
    }, {});
  }, [allPartnerTracks, allPublicTracks]);
  return {
    allTracks,
    allTracksLoading: allPublicTracksLoading || allPartnerTracksLoading,
    allTracksError: allPublicTracksError || allPartnerTracksError
  };
};
export const useTrack = (trackId, language = getLang(), skip = false) => {
  if (!language || !language.length) language = getLang();
  const {
    allTracks,
    allTracksLoading: trackLoading,
    allTracksError: trackError
  } = useAllTracks(language, skip);
  return {
    track: allTracks && allTracks[trackId],
    trackLoading,
    trackError
  };
};